import { supabase } from '../lib/supabase';
import { getFromStorage, setInStorage, generateId, STORAGE_KEYS } from './storage';
import { getCurrentTenantId } from './tenantScope';

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

const toCustomerCoupon = (row) =>
  row
    ? {
        id: row.id,
        customerId: row.customer_id,
        couponId: row.coupon_id ?? null,
        couponName: row.coupon_name || '',
        quantity: Number(row.quantity) || 1,
        rate: Number(row.rate) || 0,
        originalValue: roundMoney(row.original_value),
        remainingValue: roundMoney(row.remaining_value),
        note: row.note || '',
        tenantId: row.tenant_id || null,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        pendingSync: false,
      }
    : null;

function isMissingTableError(error) {
  const msg = error?.message || '';
  return /customer_coupons.*(does not exist|schema cache)|relation.*customer_coupons|could not find.*customer_coupons/i.test(
    msg
  );
}

function isMissingRpcError(error) {
  const msg = error?.message || '';
  return /redeem_customer_coupon|restore_customer_coupon|could not find the function|schema cache/i.test(
    msg
  );
}

function cacheList(list) {
  setInStorage(STORAGE_KEYS.CUSTOMER_COUPONS, list);
}

function upsertCacheEntry(entry) {
  if (!entry?.id) return;
  const list = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  const idx = list.findIndex((c) => c.id === entry.id);
  if (idx >= 0) list[idx] = entry;
  else list.unshift(entry);
  cacheList(list);
}

function filterByTenant(list, tenantId) {
  if (!tenantId) return list;
  return list.filter((c) => !c.tenantId || c.tenantId === tenantId);
}

function mergeWithLocalPending(fromDb, tenantId) {
  const dbIds = new Set(fromDb.map((c) => c.id));
  const local = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  const localPending = filterByTenant(local, tenantId).filter(
    (c) => c.pendingSync && c.id && !dbIds.has(c.id)
  );
  return [...fromDb, ...localPending].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
}

export async function getCustomerCoupons() {
  const tenantId = getCurrentTenantId();
  if (supabase) {
    let query = supabase
      .from('customer_coupons')
      .select('*')
      .order('created_at', { ascending: false });
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (!error && data) {
      const list = mergeWithLocalPending(data.map(toCustomerCoupon), tenantId);
      cacheList(list);
      return list;
    }
    if (tenantId && error && !isMissingTableError(error)) {
      const { data: allData, error: allError } = await supabase
        .from('customer_coupons')
        .select('*')
        .order('created_at', { ascending: false });
      if (!allError && allData) {
        const scoped = filterByTenant(allData.map(toCustomerCoupon), tenantId);
        const list = mergeWithLocalPending(scoped, tenantId);
        cacheList(list);
        return list;
      }
    }
    if (error && isMissingTableError(error)) {
      // Fall through to localStorage
    }
  }
  const all = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  return filterByTenant(all, tenantId);
}

export async function getUnusedCustomerCoupons(customerId) {
  const all = await getCustomerCoupons();
  return all.filter(
    (c) => c.customerId === customerId && roundMoney(c.remainingValue) > 0
  );
}

/**
 * Assign the same coupon type to one or more customers.
 * @param {{ customerIds: string[], coupon: { id?: string, name: string, quantity: number, rate: number }, note?: string }} data
 */
export async function assignCouponsToCustomers(data) {
  const tenantId = getCurrentTenantId();
  const customerIds = [...new Set((data.customerIds || []).filter(Boolean))];
  if (customerIds.length === 0) throw new Error('Select at least one customer');

  const quantity = Number(data.coupon?.quantity) || 1;
  const rate = Number(data.coupon?.rate) || 0;
  const originalValue = roundMoney(quantity * rate);
  if (originalValue <= 0) throw new Error('Coupon value must be greater than zero');

  const couponName = (data.coupon?.name || '').trim();
  if (!couponName) throw new Error('Coupon name is required');

  const note = (data.note || '').trim();
  const now = new Date().toISOString();
  const created = [];

  for (const customerId of customerIds) {
    const payload = {
      customer_id: customerId,
      coupon_id: data.coupon?.id || null,
      coupon_name: couponName,
      quantity,
      rate,
      original_value: originalValue,
      remaining_value: originalValue,
      note,
      tenant_id: tenantId || null,
    };

    let result = null;
    if (supabase) {
      let { data: row, error } = await supabase
        .from('customer_coupons')
        .insert(payload)
        .select()
        .single();
      if (error && payload.tenant_id) {
        const { tenant_id, ...legacy } = payload;
        ({ data: row, error } = await supabase
          .from('customer_coupons')
          .insert(legacy)
          .select()
          .single());
      }
      if (error && isMissingTableError(error)) {
        // localStorage only
      } else if (error) {
        throw new Error(error.message);
      } else if (row) {
        result = toCustomerCoupon(row);
      }
    }

    const entry = result || {
      id: generateId(),
      customerId,
      couponId: payload.coupon_id,
      couponName,
      quantity,
      rate,
      originalValue,
      remainingValue: originalValue,
      note,
      tenantId: tenantId || null,
      createdAt: now,
      updatedAt: now,
      pendingSync: true,
    };
    created.push(entry);
  }

  const existing = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  const byId = new Map(existing.map((c) => [c.id, c]));
  created.forEach((c) => byId.set(c.id, c));
  cacheList([...byId.values()].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  return created;
}

export async function updateCustomerCouponRemaining(id, remainingValue) {
  const next = roundMoney(Math.max(0, remainingValue));
  const updates = {
    remaining_value: next,
    updated_at: new Date().toISOString(),
  };

  if (supabase) {
    const { data: row, error } = await supabase
      .from('customer_coupons')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (!error && row) {
      const entry = toCustomerCoupon(row);
      upsertCacheEntry(entry);
      return entry;
    }
    if (error && !isMissingTableError(error)) {
      throw new Error(error.message);
    }
  }

  const list = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  const idx = list.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  list[idx] = {
    ...list[idx],
    remainingValue: next,
    updatedAt: updates.updated_at,
  };
  cacheList(list);
  return list[idx];
}

/**
 * Compare-and-swap fallback when RPC is unavailable.
 * Fails if remaining_value changed concurrently.
 */
async function redeemWithCompareAndSwap(id, amount) {
  const list = await getCustomerCoupons();
  const entry = list.find((c) => c.id === id);
  if (!entry) throw new Error('Customer coupon not found');
  const use = roundMoney(amount);
  if (use <= 0) return entry;
  const current = roundMoney(entry.remainingValue);
  if (current + 0.001 < use) {
    throw new Error(`Insufficient coupon balance (Nu ${current.toFixed(2)} remaining)`);
  }
  const next = roundMoney(current - use);

  if (supabase) {
    const { data: row, error } = await supabase
      .from('customer_coupons')
      .update({
        remaining_value: next,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('remaining_value', current)
      .select()
      .maybeSingle();
    if (!error && row) {
      const updated = toCustomerCoupon(row);
      upsertCacheEntry(updated);
      return updated;
    }
    if (error && isMissingTableError(error)) {
      // local path below
    } else if (error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Coupon balance changed concurrently. Refresh and try again.'
      );
    }
  }

  const local = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []);
  const idx = local.findIndex((c) => c.id === id);
  if (idx === -1) throw new Error('Customer coupon not found');
  if (roundMoney(local[idx].remainingValue) !== current) {
    throw new Error('Coupon balance changed concurrently. Refresh and try again.');
  }
  local[idx] = {
    ...local[idx],
    remainingValue: next,
    updatedAt: new Date().toISOString(),
  };
  cacheList(local);
  return local[idx];
}

/** Deduct amount from a customer coupon wallet entry. Returns updated entry. */
export async function redeemCustomerCoupon(id, amount) {
  const use = roundMoney(amount);
  if (use <= 0) {
    const list = await getCustomerCoupons();
    const entry = list.find((c) => c.id === id);
    if (!entry) throw new Error('Customer coupon not found');
    return entry;
  }

  if (supabase) {
    const { data: row, error } = await supabase.rpc('redeem_customer_coupon', {
      p_id: id,
      p_amount: use,
    });
    if (!error && row) {
      const updated = toCustomerCoupon(Array.isArray(row) ? row[0] : row);
      if (updated) {
        upsertCacheEntry(updated);
        return updated;
      }
    }
    if (error && !isMissingRpcError(error) && !isMissingTableError(error)) {
      const msg = error.message || '';
      if (/insufficient|not found/i.test(msg)) {
        throw new Error(
          msg.includes('Insufficient')
            ? msg
            : `Insufficient coupon balance or coupon not found`
        );
      }
      throw new Error(msg);
    }
  }

  return redeemWithCompareAndSwap(id, use);
}

/**
 * Deduct amount across multiple wallet entries (oldest first).
 * @returns {Promise<Array<{ id: string, amount: number }>>} allocations for rollback
 */
export async function redeemAcrossCustomerCoupons(entries, amount) {
  let left = roundMoney(amount);
  const allocations = [];
  if (left <= 0) return allocations;
  const ordered = [...(entries || [])].sort(
    (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
  );

  try {
    for (const entry of ordered) {
      if (left <= 0) break;
      // Re-read latest remaining from cache/DB path via redeem itself
      const available = roundMoney(entry.remainingValue);
      if (available <= 0) continue;
      const use = roundMoney(Math.min(available, left));
      await redeemCustomerCoupon(entry.id, use);
      allocations.push({ id: entry.id, amount: use });
      left = roundMoney(left - use);
      // Keep subsequent planned uses accurate if same list is reused
      entry.remainingValue = roundMoney(available - use);
    }
    if (left > 0.001) {
      throw new Error(`Insufficient coupon balance (short Nu ${left.toFixed(2)})`);
    }
    return allocations;
  } catch (err) {
    if (allocations.length > 0) {
      await restoreAcrossCustomerCoupons(allocations).catch(() => {});
    }
    throw err;
  }
}

/** Credit amount back onto a wallet entry. */
export async function restoreCustomerCoupon(id, amount) {
  const use = roundMoney(amount);
  if (use <= 0) {
    const list = await getCustomerCoupons();
    return list.find((c) => c.id === id) || null;
  }

  if (supabase) {
    const { data: row, error } = await supabase.rpc('restore_customer_coupon', {
      p_id: id,
      p_amount: use,
    });
    if (!error && row) {
      const updated = toCustomerCoupon(Array.isArray(row) ? row[0] : row);
      if (updated) {
        upsertCacheEntry(updated);
        return updated;
      }
    }
    if (error && !isMissingRpcError(error) && !isMissingTableError(error)) {
      throw new Error(error.message);
    }
  }

  const list = await getCustomerCoupons();
  const entry = list.find((c) => c.id === id);
  if (!entry) throw new Error('Customer coupon not found');
  return updateCustomerCouponRemaining(id, roundMoney(entry.remainingValue + use));
}

export async function restoreAcrossCustomerCoupons(allocations) {
  for (const alloc of allocations || []) {
    if (!alloc?.id || !(Number(alloc.amount) > 0)) continue;
    await restoreCustomerCoupon(alloc.id, alloc.amount);
  }
}

export async function deleteCustomerCoupon(id) {
  if (supabase) {
    const { error } = await supabase.from('customer_coupons').delete().eq('id', id);
    if (error && !isMissingTableError(error)) {
      throw new Error(error.message);
    }
  }
  const list = getFromStorage(STORAGE_KEYS.CUSTOMER_COUPONS, []).filter((c) => c.id !== id);
  cacheList(list);
  return true;
}

export function getCustomerCouponBalance(customerCoupons, customerId) {
  return roundMoney(
    (customerCoupons || [])
      .filter((c) => c.customerId === customerId)
      .reduce((sum, c) => sum + (Number(c.remainingValue) || 0), 0)
  );
}
